import { PrintAreaType } from '../interfaces/Order';

// T-Shirt Colors (lowercase values for image file naming)
export const TSHIRT_COLORS = [
  { label: 'White', value: 'white' },
  { label: 'Black', value: 'black' },
  { label: 'Red', value: 'red' },
  { label: 'Navy Blue', value: 'navy' },
  { label: 'Royal Blue', value: 'blue' },
  { label: 'Green', value: 'green' },
  { label: 'Yellow', value: 'yellow' },
  { label: 'Orange', value: 'orange' },
  { label: 'Purple', value: 'purple' },
  { label: 'Gray', value: 'gray' },
];

// Print Area Options for dropdown selector
export const PRINT_AREA_OPTIONS = [
  {
    label: 'Giữa ngực trái',
    value: PrintAreaType.LeftChest,
    description: 'Small logo area (3-4 inches)',
  },
  {
    label: 'Giữa ngực nhỏ',
    value: PrintAreaType.CenterChestSmall,
    description: 'Small design area',
  },
  {
    label: 'Giữa ngực',
    value: PrintAreaType.CenterChest,
    description: 'Medium design area',
  },
  {
    label: 'Toàn thân',
    value: PrintAreaType.FullFront,
    description: 'Large print area',
  },
];

export const PRINT_AREA_OPTIONS_LABELS: Record<PrintAreaType, string> = {
  [PrintAreaType.LeftChest]: 'Giữa ngực trái',
  [PrintAreaType.CenterChestSmall]: 'Giữa ngực nhỏ',
  [PrintAreaType.CenterChest]: 'Giữa ngực',
  [PrintAreaType.FullFront]: 'Toàn thân',
  [PrintAreaType.BagSmallCenter]: 'Giữa túi (nhỏ)',
  [PrintAreaType.BagNormalCenter]: 'Giữa túi (tiêu chuẩn)',
  [PrintAreaType.BagFullCenter]: 'Toàn túi',
};

export const TshirtPrintArea = [
  PrintAreaType.LeftChest,
  PrintAreaType.CenterChestSmall,
  PrintAreaType.CenterChest,
  PrintAreaType.FullFront,
];

export const BagPrintArea = [
  PrintAreaType.BagSmallCenter,
  PrintAreaType.BagNormalCenter,
  PrintAreaType.BagFullCenter,
];

// Print Area Configuration for T-Shirts (adjusted for real t-shirt images)
export const TSHIRT_PRINT_AREAS: Record<
  PrintAreaType,
  {
    x: number;
    y: number;
    width: number;
    height: number;
    maxWidth: number;
    maxHeight: number;
    description: string;
  }
> = {
  [PrintAreaType.LeftChest]: {
    x: 320, // Wearer's left chest, avoiding arm overlap
    y: 120, // Moved down to chest area
    width: 80,
    height: 60,
    maxWidth: 100,
    maxHeight: 80,
    description:
      'Khu vực logo nhỏ ở ngực trái (trái của người mặc, xuất hiện bên phải trong mockup)',
  },
  [PrintAreaType.CenterChestSmall]: {
    x: 260, // Centered on t-shirt
    y: 120, // Positioned in chest area
    width: 80,
    height: 60,
    maxWidth: 100,
    maxHeight: 80,
    description: 'Khu vực logo nhỏ ở giữa ngực',
  },
  [PrintAreaType.CenterChest]: {
    x: 225, // Centered on t-shirt
    y: 140, // Positioned in chest area
    width: 150,
    height: 100,
    maxWidth: 180,
    maxHeight: 120,
    description: 'Khu vực thiết kế vừa ở giữa ngực',
  },
  [PrintAreaType.FullFront]: {
    x: 215, // Perfectly centered on t-shirt
    y: 140, // Start lower to avoid collar area
    width: 170,
    height: 210,
    maxWidth: 190,
    maxHeight: 230,
    description: 'Khu vực in lớn với lề phù hợp từ các cạnh và đường may',
  },
  [PrintAreaType.BagSmallCenter]: {
    x: 276, // Centered on bag
    y: 250, // Positioned in upper center area
    width: 48,
    height: 48,
    maxWidth: 60,
    maxHeight: 60,
    description: 'Khu vực thiết kế nhỏ ở giữa túi',
  },
  [PrintAreaType.BagNormalCenter]: {
    x: 225, // Centered on bag
    y: 290, // Positioned in center area
    width: 150,
    height: 80,
    maxWidth: 180,
    maxHeight: 80,
    description: 'Khu vực thiết kế vừa ở giữa túi',
  },
  [PrintAreaType.BagFullCenter]: {
    x: 225, // Centered on bag
    y: 300, // Positioned to cover most of bag
    width: 150,
    height: 140,
    maxWidth: 190,
    maxHeight: 230,
    description: 'Khu vực thiết kế lớn trên toàn bộ túi',
  },
};

// T-Shirt Sizes
export const TSHIRT_SIZES = [
  { label: 'XS', value: 'xs' },
  { label: 'S', value: 's' },
  { label: 'M', value: 'm' },
  { label: 'L', value: 'l' },
  { label: 'XL', value: 'xl' },
  { label: 'XXL', value: 'xxl' },
  { label: 'XXXL', value: 'xxxl' },
];

// Initial canvas settings
export const TSHIRT_CANVAS_CONFIG = {
  width: 600,
  height: 500,
  initialZoom: 1,
  tshirtScale: 0.8,
  tshirtOffsetX: 50,
  tshirtOffsetY: 20,
};

// Print methods and their constraints
export const PRINT_METHODS = [
  {
    label: 'Screen Printing',
    value: 'screen-printing',
    minQuantity: 12,
    maxColors: 6,
    description: 'Best for large quantities, vibrant colors',
  },
  {
    label: 'Direct-to-Garment (DTG)',
    value: 'dtg',
    minQuantity: 1,
    maxColors: 'unlimited',
    description: 'Perfect for detailed designs, photo-quality prints',
  },
  {
    label: 'Heat Transfer Vinyl',
    value: 'htv',
    minQuantity: 1,
    maxColors: 5,
    description: 'Great for text and simple designs, durable',
  },
  {
    label: 'Sublimation',
    value: 'sublimation',
    minQuantity: 1,
    maxColors: 'unlimited',
    description: 'Full-color prints, works best on light-colored shirts',
  },
];

export const DesignMaxSize = 1024 * 1024 * 30; // 30MB
